import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { FormikHelpers } from 'formik';
import { message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { DateFormat } from '@shared/constants';
import { localURLMaker } from '@shared/utils';
import { CashFlowTransactionsService } from '../../services';

export type CashFlowTransactionFormValues = {
  type: 'income' | 'expense' | 'transfer';
  cashRegisterId: string;
  amount: string;
  target: { cashRegisterId: string; amount: string };
  operationIds: string[];
  incomeOperationIds: string[];
  operatedAt: Dayjs | null;
  description: string;
};

export const useCashFlowTransactionForm = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const detail = useQuery(
    ['cash-flow-transaction', id],
    async () => {
      const result = await CashFlowTransactionsService.getById(id!);
      if (result.status === 200) return result.data;
      throw new Error(result.data as string);
    },
    { enabled: !!id, cacheTime: 0 },
  );

  const initialValues = useMemo<CashFlowTransactionFormValues>(() => {
    if (id && detail.data) {
      const d = detail.data;
      return {
        type: d.isTransfer ? 'transfer' : d.type,
        cashRegisterId: String(d.cashRegister.id),
        amount: String(d.amount),
        target: { cashRegisterId: d.target ? String(d.target.cashRegister.id) : '', amount: d.target ? String(d.target.amount) : '' },
        operationIds: [String(d.operation.id), String(d.operation.child.id)],
        incomeOperationIds: d.targetOperation ? [String(d.targetOperation.id), String(d.targetOperation.child.id)] : [],
        operatedAt: d.operatedAt ? dayjs(d.operatedAt) : null,
        description: d.description || '',
      };
    }
    return { type: 'income', cashRegisterId: '', amount: '', target: { cashRegisterId: '', amount: '' }, operationIds: [], incomeOperationIds: [], operatedAt: dayjs(), description: '' };
  }, [id, detail.data]);

  const onSubmit = useCallback(
    async (values: CashFlowTransactionFormValues, helpers: FormikHelpers<CashFlowTransactionFormValues>) => {
      const result = await CashFlowTransactionsService.create({
        id,
        type: values.type,
        cashRegisterId: values.cashRegisterId,
        amount: values.amount,
        operationIds: values.operationIds,
        incomeOperationIds: values.incomeOperationIds,
        transferCashRegisterId: values.target.cashRegisterId,
        transferAmount: values.target.amount,
        operatedAt: values.operatedAt ? values.operatedAt.format(DateFormat.DATE_TIME) : dayjs().format(DateFormat.DATE_TIME),
        description: values.description,
      });
      if (result.status === 200) {
        message.success(id ? 'Dəyişikliklər saxlanıldı.' : 'Tranzaksiya yaradıldı.');
        navigate(localURLMaker('/cash-flow/transactions', {}, { reFetchCashFlowTransactionsTable: '1' }));
      } else if (result.status === 422) {
        const e = result.data as Record<string, string>;
        helpers.setErrors({
          cashRegisterId: e.cashbox_id,
          amount: e.amount,
          target: { cashRegisterId: e.transfer_cashbox_id, amount: e.transfer_amount },
          operationIds: e.category_id as any,
          incomeOperationIds: e.category_id_second as any,
          operatedAt: e.operation_date as any,
          description: e.descr,
        });
      } else {
        message.error(result.data as string);
      }
      helpers.setSubmitting(false);
    },
    [id, navigate],
  );

  return { initialValues, onSubmit, id, isLoading: !!id && (detail.isLoading || !detail.data) };
};
